import {Field, useField} from 'formik';
import {useCallback, useEffect, useState, useMemo} from 'react';
import {useDropzone} from 'react-dropzone';
import styled from 'styled-components';

import {FieldMargin} from '../../_old/FieldHelpers/FieldContainers';
import {FormError} from '../../_old/FieldHelpers/FormError';
import {ProtectedImage} from '../../components/Protected';
import {scToTwColour} from '../../theme';

import {DropzoneLabel} from './common';
import Dropzone from './Dropzone';
import {getAcceptLabels, getAcceptTypes} from './functions';

const DEFAULT_IMAGE_ACCEPTED = 'image/jpeg,image/png';

type SingleFileUploadInnerProps = React.HTMLAttributes<HTMLDivElement> & {
  name: string;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
};
function SingleFileUploadInner(
  props: SingleFileUploadInnerProps
): React.JSX.Element {
  const [isHovered, setIsHovered] = useState(false);

  const [{value}, , {setValue, setError, setTouched}] =
    useField<SingleFileField2>(props.name);
  const accept = props.inputProps?.accept || DEFAULT_IMAGE_ACCEPTED;
  const [shortLabel, fullLabel] = getAcceptLabels(accept);

  // cache whether initial value from outside is blob or image type
  const urlType = useMemo(() => typeof value.existing, [value.existing]);

  const {getRootProps, getInputProps, open} = useDropzone({
    accept: getAcceptTypes(accept),
    multiple: false,
    onDropAccepted: (files) => {
      setValue({
        // should create thumbnail url for image files
        // determine thumbnail visibility on value.existing type
        existing: files[0].type.includes('image/')
          ? URL.createObjectURL(files[0])
          : true,
        file: files[0],
        initial: value.initial,
      });
      setError('');
      setTouched(true, false);
    },
    onDropRejected: (rejects) => {
      const error = rejects[0].errors[0];
      const errorStr =
        error.code === 'file-invalid-type'
          ? `File type must be the following: ${fullLabel}`
          : error.message;
      setError(errorStr);
      setTouched(true, false);
      setIsHovered(false);
    },
    onDragEnter: () => setIsHovered(true),
    onDragLeave: () => setIsHovered(false),
  });

  const resetThumb = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>): void => {
      e.stopPropagation();
      setValue({
        file: null,
        existing: urlType === 'string' ? '' : false,
        initial: value.initial,
      });
      setError('');
    },
    [setError, setValue, urlType, value.initial]
  );

  // Cleanup local memory
  useEffect(() => {
    return () => {
      typeof value.existing === 'string' && URL.revokeObjectURL(value.existing);
    };
  }, [value.existing]);

  // html required attr not compatible with react-dropzone
  // red * is still derived from input props
  // actual validation should be done by formik
  const {required, ...restInputProps} = props.inputProps || {};

  return (
    <StyledDropzone {...getRootProps()} $isHovered={isHovered}>
      <input
        name={props.name}
        id={props.name}
        {...restInputProps}
        {...getInputProps()}
      />

      {!value.existing ? (
        <DropzoneLabel acceptLabels={shortLabel} isHovered={isHovered} />
      ) : (
        <>
          {typeof value.existing === 'string' ? (
            <ProtectedImage
              src={value.existing}
              alt="thumbnail"
            />
          ) : (
            <Count>File accepted</Count>
          )}

          <Controls>
            <button
              type="button"
              className="btn-secondary"
              onClick={(e) => {
                e.stopPropagation();
                open();
              }}
            >
              Edit
            </button>
            <button type="button" className="btn-danger" onClick={resetThumb}>
              Clear
            </button>
          </Controls>
        </>
      )}
    </StyledDropzone>
  );
}

const Controls = styled.div`
  display: none;
  justify-content: flex-end;
  // margin-top: 0.5rem;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`;
const Count = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: center;
  color: ${scToTwColour('theme.colours.white')};
  font-size: 1.2rem;
  line-height: 1em;
  padding: 2em 0.5em;
  background-color: ${scToTwColour('theme.colours.blue9')};
`;
const StyledDropzone = styled(Dropzone)`
  position: relative;
  max-width: 40rem;

  & img {
    max-height: 40rem;
  }

  &:hover {
    img {
      filter: brightness(0.8);
    }
    ${Controls} {
      display: flex;
    }
    ${Count} {
      color: ${scToTwColour('theme.colours.blue11')};
    }
  }
`;

type SingleFileUploadProps = SingleFileUploadInnerProps & {
  children: React.ReactNode;
};
export function SingleFileUpload2(
  props: SingleFileUploadProps
): React.JSX.Element {
  const {name, children, ...restProps} = props;
  const [, {error, touched}] = useField<SingleFileField2>(name);

  return (
    <FieldMargin>
      <Label
        className="small-upper"
        required={!!restProps.inputProps?.required}
      >
        {props.children}
      </Label>

      <Field name={name}>
        {() => <SingleFileUploadInner name={name} {...restProps} />}
      </Field>

      {error &&
        touched &&
        // @ts-expect-error formik zod not sure idk
        error._errors?.map((error) => (
          <FormError key={error}>{error}</FormError>
        ))}
    </FieldMargin>
  );
}

export type SingleFileField2 = {
  file: File | null;
  existing: string | boolean;
  initial: boolean;
};

const Label = styled.p<{required: boolean}>`
  position: relative;
`;
